import _ from 'lodash'
import store from '@/Setup/SetupStore.js'
import { AUTH_USER_GETTER } from '@/Modules/Auth/AuthModule.js'
import { UPDATE_ENDORSEMENT_VALUE } from '@/Modules/Quote/Components/QuoteForm/QuoteFormModule.js'
export default class BaseNodeResolver {
  constructor(baseNode) {
    this.baseNode = baseNode
  }

  static SECTION_TABLE_KEY() {
    return 'sectionTableRows'
  }

  static TRIGGER_ONLOAD() {
    return 'onLoad'
  }

  static JOINT_NODE() {
    return 'properties'
  }

  static TARGET_NOT_FOUND_ERROR(nodeName = 'node') {
    return new Error(`Target ${nodeName} is not found`)
  }

  getValueByRuleOrSource(rule) {
    if (rule.source) {
      return this.getValueFromRuleSource(rule.source)
    }

    return this.getRuleValue(rule)
  }

  getValueFromRuleSource(source) {
    if (_.isArray(source)) {
      return _.map(source, (item) => {
        return this.getValue(item)
      })
    }

    return this.getValue(source)
  }

  getRuleValue(rule) {
    let value = rule.value

    if (typeof value === 'object' && value !== null) {
      if (_.isArray(value)) {
        return value
      }

      if (!value.value) {
        return null
      }

      value = value.value
    }

    return value
  }

  getValue(key) {
    let value = _.get(
      this.baseNode,
      `properties.${this.getPathToNode(key)}.value`
    ) ?? _.get(this.baseNode, `${key}`, null)

    if (
      value !== null &&
      typeof value === 'object' &&
      value.hasOwnProperty('value')
    ) {
      value = value.value
    }

    return value
  }

  getUiOptions(key) {
    return _.get(
      this.baseNode,
      `properties.${this.getPathToNode(key)}.uiOptions`,
      {}
    )
  }

  insertTableSectionToPath(path = '', separator = '.') {
    return path
      .split(separator)
      .reduce((acc, current) => {
        acc.push(BaseNodeResolver.JOINT_NODE(), current)
        const baseNode = _.get(this.baseNode, acc)
        if (!baseNode) {
          return acc
        }
        if (baseNode.uiType === 'tableSection') {
          acc.push(BaseNodeResolver.SECTION_TABLE_KEY())
        }
        return acc
      }, [])
      .filter((item) => item !== BaseNodeResolver.JOINT_NODE())
      .join(separator)
  }

  getPathToNode(property) {
    /**
     * Assume to get raw path as an argument and prepare it for tableSection
     */
    let path = this.parseTableSectionUiRuleParam(property)
    if (!_.isString(path)) {
      return property
    }

    if (!path.match(new RegExp(BaseNodeResolver.SECTION_TABLE_KEY()))) {
      path = this.insertTableSectionToPath(path)
    }

    if (path.match(new RegExp(BaseNodeResolver.SECTION_TABLE_KEY()))) {
      return path
        .split(`.${BaseNodeResolver.SECTION_TABLE_KEY()}`)
        .map((item, index) => {
          if (index === 0) {
            return item
              .replace(/\./g, '.properties.')
              .replace(/_/g, '.')
              .replace('properties.rows', 'rows')
              .concat(`.${BaseNodeResolver.SECTION_TABLE_KEY()}`)
          }
          return item
        })
        .join('')
        .replace(/\.node$/, '')
        .concat('.node')
    }

    if (path.match(/{\d}/)) {
      try {
        const index = _.first(_.first(property.match(/{\d}/)).match(/\d/))

        const multipleSectionNode =
          this.baseNode.properties[
          _.first(property.split(/\.{\d}\./)).replace('.', '.properties.')
          ]
        const multipleSectionNodeRowKey = Object.keys(multipleSectionNode.rows)[
          index
        ]
        path = property.replace(/{\d}\./, `rows_${multipleSectionNodeRowKey}_`)
      } catch (error) {
        console.error(error)
      }
    }

    if (path.match(/<\d>/)) {
      try {
        const index = _.first(_.first(property.match(/<\d>/)).match(/\d/))
        let nodePath = _.first(property.split(/<\d>\./)).replace('.', '.properties.');
        nodePath = nodePath.slice(0, -1);
        const endorsementNode = _.get(this.baseNode.properties, nodePath)

        const endorsementNodeValues = Object.keys(endorsementNode.value)[
          index
        ]

        path = property.replace('.', '.properties.').replace(/<\d>\./, `value.${endorsementNodeValues}.`)

        return path;
      } catch (error) {
        console.error(error)
      }
    }

    return path
      .replace(/\./g, '.properties.') // Replace all dots to properties, to suit the node structure.
      .replace(/_/g, '.') // Underscores are inserted if properties should not be inserted.
      .replace('properties.rows', 'rows') // Properties are never needed next to rows.
  }

  parseTableSectionUiRuleParam(param = '') {
    /**
     * @todo Do you really need '.node' in the path?
     */
    const regExp = new RegExp(/\.column\./i)
    if (!_.isString(param) || !param.match(regExp)) {
      return param
    }
    const newPath = param
      .replace(regExp, `.${BaseNodeResolver.SECTION_TABLE_KEY()}.`)
      .concat('.node')
    return newPath
  }

  setVisibility(target, state) {
    if (_.isArray(target)) {
      target.forEach((item) => {
        this.setVisibilityByTarget(item, state)
      })
      return
    }

    this.setVisibilityByTarget(target, state)
  }

  visibleByPermission(node) {
    const respectPermission = _.get(node, 'uiOptions.respectPermissionDuringUiRules');
    if (!respectPermission) return true;

    const permissionKey = 'uiOptions.permission';
    const permission = _.get(node, permissionKey);

    if (!permission) {
      console.warn(`[BaseNodeResolver.visibleByPermission] Node has respectPermissionDuringUiRules set but no permission. Please set ${permissionKey}`, node);
      return true;
    }

    if (typeof permission !== 'string') {
      console.error(`[BaseNodeResolver.visibleByPermission] ${permissionKey} must be a string.`, node);
      return true;
    }

    const user = store.getters['AuthModule/' + AUTH_USER_GETTER]

    if (!user) {
      console.error('[BaseNodeResolver.visibleByPermission] Failed getting user.', node);
      return false;
    }

    return user.permissions.includes(permission);
  }

  setVisibilityByTarget(target, state) {
    const node = _.get(this.baseNode, `properties.${this.getPathToNode(target)}`)

    if (node) {
      if (!this.visibleByPermission(node)) {
        return;
      }
      const nodePath = `properties.${this.getPathToNode(target)}`
      _.set(this.baseNode, `${nodePath}.uiOptions.visible`, state)

      this.resetOnHide(nodePath, node, state)
    } else {
      console.warn(`[UiRules] Target node for visibility not found: ${target}`)
    }
  }

  resetOnHide(nodePath, node, state) {
    if (!state && node.uiOptions?.resetOnHide === true && ['radio', 'select'].includes(node.uiType)) {
      _.set(this.baseNode, `${nodePath}.value`, node.default ?? null)
    }
  }

  setValueToNode(target, value, trigger) {
    if (_.isArray(target)) {
      target.forEach((item) => {
        this.setValueToNodeByTarget(item, value, trigger)
      })
      return
    }

    this.setValueToNodeByTarget(target, value, trigger)
  }

  setValueToNodeByTarget(target, value, trigger) {
    const formattedPath = `properties.${this.getPathToNode(target)}`
    const targetNode = _.get(this.baseNode, formattedPath)

    if (_.isUndefined(targetNode)) {
      throw BaseNodeResolver.TARGET_NOT_FOUND_ERROR(formattedPath)
    }

    // Check if the formattedPath is for an endorsement
    // big assumption that if the path ends in .value.{number}.{property}
    // it is an endorsement, e.g. endorsements.value.0.limit, otherwise every . would be followed by property
    // another way would be to try to get parent node and check uitype
    const IS_ENDORSEMENT = /\.value\.\d+\.\w+/.test(formattedPath)
    if (IS_ENDORSEMENT) {
      // Endorsements are special, instead of having a value property, the whole node is the value.
      // e.g. {uiType: 'text', value: 'test'}, {uiType: 'endorsements', value: {limit: 1, deductible: 2, ...}}
      // We need to set the value to the specific property of the endorsement node.

      // assuming that endorsement values will never be nested.
      const property = formattedPath.split('.').pop()
      const section = formattedPath.split('.value.')[0].split('.').pop()
      const index = formattedPath.split('.value.')[1].split('.')[0]
      const currentValue = targetNode?.[property]

      const triggerName = _.isObject(trigger) ? trigger.name : trigger
      const force = _.isObject(trigger) ? trigger.force : false

      if (
        currentValue !== null &&
        currentValue !== '' &&
        triggerName === BaseNodeResolver.TRIGGER_ONLOAD() &&
        !force
      ) {
        return
      }

      store.dispatch(`QuoteFormModule/${UPDATE_ENDORSEMENT_VALUE}`, {
        section,
        index,
        key: property,
        value,
      })

      _.set(
        this.baseNode,
        formattedPath,
        value
      )
    } else {
      const currentValue = targetNode.value

      const triggerName = _.isObject(trigger) ? trigger.name : trigger
      const force = _.isObject(trigger) ? trigger.force : false

      if (
        currentValue !== null &&
        currentValue !== '' &&
        triggerName === BaseNodeResolver.TRIGGER_ONLOAD() &&
        !force
      ) {
        return
      }

      _.set(
        this.baseNode,
        `${formattedPath}.value`,
        value
      )
    }
  }

  getValueBySourceOrParameters(rule) {
    if (rule.source) {
      return this.getValueFromRuleSource(rule.source)
    }

    return rule.parameters
  }

  parseParameters(parameters) {
    if (typeof parameters === 'string') {
      return parameters.split(',')
    }

    if (typeof parameters === 'number') {
      return [parameters]
    }

    if (typeof parameters === 'boolean') {
      return [parameters ? '1' : '0']
    }

    if (!parameters) {
      return []
    }

    return parameters
  }

  setVisibilityByParameters(
    rule,
    value,
    shouldInclude = true,
    valueKey = 'value'
  ) {
    const parameters = this.parseParameters(rule.parameters)
    let visibility

    if (_.isArray(value)) {
      visibility = value.some((item) => {
        if (_.isObject(item) && item[valueKey]) {
          return parameters.includes(item[valueKey])
        }
        return parameters.includes(item)
      })
    } else {
      visibility = parameters.includes(`${value}`)
    }

    if (visibility) {
      this.setVisibility(rule.target, !shouldInclude)
    } else {
      this.setVisibility(rule.target, shouldInclude)
    }
  }

  getMultipleSourceValue(sources) {
    if (_.isArray(sources)) {
      return sources.map((item) => this.getValueFromRuleSource(item))
    }
    return []
  }

  static collectHiddenFieldKeys(properties) {
    const keys = []

    const walk = (props) => {
      if (!props) {
        return
      }

      Object.values(props).forEach((node) => {
        if (node.internal) {
          if (node.properties) {
            walk(node.properties)
          }
          return
        }

        const isHidden = node.uiOptions?.visible === false
        const isSchemaHidden = node.uiType === 'hidden'
        const isPermissionHidden = node.uiOptions?.respectPermissionDuringUiRules === true
          && typeof node.uiOptions?.permission === 'string'
          && (() => {
            const user = store.getters['AuthModule/' + AUTH_USER_GETTER]
            return user ? !user.permissions.includes(node.uiOptions.permission) : true
          })()

        if (isHidden && !isSchemaHidden && !isPermissionHidden) {
          keys.push(node.dataKey.replace(/^data\./, ''))
        }

        if (node.properties) {
          walk(node.properties)
        }
      })
    }

    walk(properties)
    return keys
  }
}
